import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  AlertCircle, BookOpen, Calendar, Clock, Flame, Heart,
  Moon, Music, Sparkles, Star, Sun, Sunrise, Sunset,
} from 'lucide-react';

/**
 * תבנית באנר — פריסה אופקית של מדליונים/תגיות מכובדות.
 * כל פריט מוצג כמדליון עגול-מלבני בזהב על רקע נייבי.
 */

const NAVY = '#0d1b2a';
const NAVY_MID = '#162a40';
const GOLD = '#C9A227';
const GOLD_LIGHT = '#e6c45e';
const IVORY = '#F5F0E8';
const font = 'Frank Ruhl Libre, David, serif';

const CATEGORY_ICONS = {
  tachanun: AlertCircle, hallel: Music, prayerAdditions: BookOpen,
  seasonalPrayers: Sunrise, seasonalTransitions: Sun, selichot: Heart,
  weekdayPrayers: Clock, shabbatAdditions: Flame, motzaeiShabbat: Moon,
  seasonalCustoms: Star, liturgy: Sparkles, leyning: BookOpen,
  haftarah: BookOpen, mourningPeriods: Sunset, general: Calendar,
  limudim: BookOpen,
};

/* ── מדליון בודד ── */
const Medallion = ({ item }) => {
  const isHigh = item.priority === 'high';
  const IconComp = CATEGORY_ICONS[item.category] || Calendar;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 0.4, minWidth: 100, maxWidth: 140, flex: '0 0 auto',
    }}>
      {/* עיגול אייקון */}
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isHigh
          ? `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`
          : `rgba(201,162,39,0.12)`,
        border: `1.5px solid ${isHigh ? GOLD : 'rgba(201,162,39,0.3)'}`,
        boxShadow: isHigh ? `0 0 10px rgba(201,162,39,0.3)` : 'none',
      }}>
        <IconComp size={15} style={{ color: isHigh ? NAVY : GOLD_LIGHT }} />
      </Box>

      {/* תווית */}
      <Typography sx={{
        fontFamily: font, fontSize: '0.62rem', fontWeight: 700,
        color: isHigh ? GOLD : IVORY, textAlign: 'center',
        lineHeight: 1.3,
      }}>
        {item.label}
      </Typography>

      {/* ערך */}
      {item.value && (
        <Typography sx={{
          fontFamily: font, fontSize: '0.54rem', fontWeight: 500,
          color: 'rgba(245,240,232,0.7)', textAlign: 'center',
          lineHeight: 1.2,
        }}>
          {item.value}
        </Typography>
      )}

      {/* פירוט */}
      {item.detail && (
        <Typography sx={{
          fontFamily: font, fontSize: '0.48rem', fontWeight: 400,
          color: 'rgba(245,240,232,0.45)', textAlign: 'center',
          lineHeight: 1.2,
        }}>
          {item.detail}
        </Typography>
      )}
    </Box>
  );
};

/* ── Lookahead יום ── */
const LookaheadDay = ({ dayData }) => (
  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(201,162,39,0.15)' }}>
    <Typography sx={{
      fontFamily: font, fontSize: '0.6rem', fontWeight: 700,
      color: GOLD, textAlign: 'center', mb: 0.75,
      letterSpacing: '0.06em',
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{ fontFamily: font, fontSize: '0.55rem', color: 'rgba(245,240,232,0.4)', textAlign: 'center' }}>
        אין ענייני יום מיוחדים
      </Typography>
    ) : (
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
        gap: 1.5,
      }}>
        {dayData.items.map((item, i) => (
          <Medallion key={item.id || `la-${i}`} item={item} />
        ))}
      </Box>
    )}
  </Box>
);

/* ══ Banner Template ══ */
const Banner = ({
  today, items, lookaheadItems, lookaheadDays,
  loading, error, title, showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const displayItems = items || [];

  return (
    <Box sx={{
      width: '100%', height: '100%',
      background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`,
      direction: 'rtl', fontFamily: font,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* מסגרת זהב עדינה */}
      <Box sx={{
        position: 'absolute', inset: 5,
        border: '1px solid rgba(201,162,39,0.2)',
        borderRadius: '4px', pointerEvents: 'none',
      }} />

      <Box sx={{
        flex: 1, overflow: 'auto', p: 1.5,
        '&::-webkit-scrollbar': { width: 2 },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(201,162,39,0.3)', borderRadius: 1 },
      }}>
        {/* כותרת */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          {showTitle && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.58rem', fontWeight: 500,
              color: 'rgba(201,162,39,0.6)', letterSpacing: '0.15em',
            }}>
              {title}
            </Typography>
          )}
          {hebrewDateStr && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.82rem', fontWeight: 700,
              color: GOLD_LIGHT,
              textShadow: '0 0 15px rgba(201,162,39,0.25)',
            }}>
              {hebrewDateStr}
            </Typography>
          )}
          {/* מפריד */}
          <Box sx={{
            height: '1px', mt: 0.6, mx: 'auto', maxWidth: '60%',
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            opacity: 0.3,
          }} />
        </Box>

        {/* Loading / Error */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} sx={{ color: GOLD }} />
          </Box>
        )}
        {error && (
          <Typography sx={{ fontSize: '0.6rem', color: '#ef4444', textAlign: 'center' }}>
            שגיאה בטעינה
          </Typography>
        )}

        {/* ריק */}
        {displayItems.length === 0 && !loading && (
          <Typography sx={{ fontFamily: font, fontSize: '0.65rem', color: 'rgba(245,240,232,0.4)', textAlign: 'center', mt: 2 }}>
            אין ענייני יום מיוחדים להיום
          </Typography>
        )}

        {/* מדליונים — פריסה אופקית */}
        {displayItems.length > 0 && (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
            gap: 1.5, py: 0.5,
          }}>
            {displayItems.map((item, i) => (
              <Medallion key={item.id || `item-${i}`} item={item} />
            ))}
          </Box>
        )}

        {/* Lookahead */}
        {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
          lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default Banner;
